import type {ReactElement} from 'react';

import type {Participant, ParticipantID, RoomID} from '@pexip/infinity';
import type {DeniedDevices} from '@pexip/media';
import type {MediaDeviceInfoLike} from '@pexip/media-control';
import type {
    InboundAudioMetrics,
    InboundVideoMetrics,
    OutboundAudioMetrics,
    OutboundVideoMetrics,
} from '@pexip/peer-connection-stats';

import type {PanelAnimationTypes} from './utils/meetingPanels';

export type {ParticipantID, RoomID, Participant} from '@pexip/infinity';

export type DeviceError = {
    title: string;
    description?: string | ReactElement;
    deniedDevice?: DeniedDevices;
    retriable?: boolean;
    /** Whether retrying is the only recovery action available. */
    retryOnly?: boolean;
};

export type DeviceErrors = {videoError: DeviceError; audioError: DeviceError};

/** Display information and controls for one media input status. */
export interface DeviceStatusInfo {
    /** Controls shown with the status. */
    controls?: 'media' | 'permissions' | 'permissions-dismiss';
    /** Input whose terminal error is displayed. */
    error?: DeniedDevices;
    /** Whether the failed input acquisition can be retried. */
    retriable?: boolean;
    /** Whether retrying is the only recovery action available. */
    retryOnly?: boolean;
    /** Detailed status copy. */
    message: string | ReactElement;
    /** Status heading. */
    title: string;
    /** Input whose non-terminal warning is displayed. */
    warning?: DeniedDevices;
}

/** Display information for both user-media inputs. */
export interface InputDeviceStatusInfo {
    /** Audio input display information. */
    audio: DeviceStatusInfo;
    /** Video input display information. */
    video: DeviceStatusInfo;
}

export type AudioMeterState = {level: number; peak: number; enabled?: boolean};

export enum PreflightJoinDetailsToRender {
    BlockedDeviceSelection = 'blocked-device-selection',
    DeviceSelection = 'device-selection',
    Spinner = 'spinner',
}

export enum BlockedBrowserPermissionsInfoType {
    Void = 'void',
    CompleteReload = 'complete-reload',
    Complete = 'complete',
}

export type MeetingPanelType =
    | 'participants'
    | 'chat'
    | 'add'
    | 'breakoutRooms'
    | 'plugins'
    | 'transcripts'
    | 'meetingManagerRaisedHand'
    | 'meetingManagerWaitingInLobby'
    | 'meetingManagerPlugins'
    | 'meetingManagerBreakoutRooms'
    | 'meetingManagerBreakoutRoomsAskingForHelp';

export type MeetingPanelsState = {
    openBreakoutRoomsPanel: boolean;
    openChatPanel: boolean;
    openParticipantPanel: boolean;
    openPluginsPanel: boolean;
    openAddParticipant: boolean;
    openTranscriptsPanel: boolean;
    openMeetingManagerRaisedHand: boolean;
    openMeetingManagerWaitingInLobby: boolean;
    openMeetingManagerPlugins: boolean;
    openMeetingManagerBreakoutRooms: boolean;
    openMeetingManagerBreakoutRoomsAskingForHelp: boolean;
    animationType: PanelAnimationTypes;
};

export type MeetingPanelActions = {
    togglePanels: (panel: MeetingPanelType) => void;
};

export enum PresentationType {
    Images = 'images',
    Video = 'video',
}

export enum PresentationEmphasis {
    Primary = 'primary',
    Secondary = 'secondary',
}

export enum PresentationSize {
    Large = 'large',
    Full = 'full',
}

export enum PresentationAction {
    Start = 'start',
    Reset = 'reset',
    Receive = 'receive',
    SetLocalMediaStream = 'setLocalMediaStream',
    SetRemoteMediaStream = 'setRemoteMediaStream',
    SetEmphasis = 'setEmphasis',
    SetExpandPrimary = 'setExpandPrimary',
    SetShowSteal = 'setShowSteal',
    SetSize = 'setSize',
    SetPresenterName = 'setPresenterName',
    ActivityChange = 'ActivityChange',
}

export type PresentationEvent =
    | {type: PresentationAction.SetLocalMediaStream; mediaStream?: MediaStream}
    | {type: PresentationAction.SetRemoteMediaStream; mediaStream?: MediaStream}
    | {type: PresentationAction.SetEmphasis; emphasis: PresentationEmphasis}
    | {type: PresentationAction.SetExpandPrimary; expandPrimary: boolean}
    | {type: PresentationAction.SetShowSteal; showSteal: boolean}
    | {type: PresentationAction.SetSize; size: PresentationSize}
    | {
          type: PresentationAction.SetPresenterName;
          presenterName?: string;
      }
    | {
          type: PresentationAction.ActivityChange;
          activity: PresentationActivityState;
      };

export interface PresentationActivityState {
    send: boolean;
    recv: boolean;
}

export interface PresentationState {
    emphasis?: PresentationEmphasis;
    expandPrimary?: boolean;
    showSteal?: boolean;
    size?: PresentationSize;
    localMediaStream?: MediaStream;
    remoteMediaStream?: MediaStream;
    activity?: PresentationActivityState;
    presenterName?: string;
}

export enum StreamStatus {
    /* BrowserPip - stream is currently within the browser picture-in-picture window */
    BrowserPip = 'BrowserPip',

    /* Emphasized - stream fills as much of the stage as possible */
    Emphasized = 'Emphasized',

    /* External - equivalent to being popped out in a separate window */
    /* note: currently only used by the presentation stream */
    /* note: perhaps utilized in the future by the meeting mix stream */
    External = 'External',

    /* Deemphasized - stream fills any leftover space on the stage */
    Deemphasized = 'Deemphasized',

    /* Expanded - stream is maximized to fill the entire stage */
    Expanded = 'Expanded',

    /* Pip - stream is minimized to occupy a small pip */
    Pip = 'Pip',

    /* None - stream currently has no significant status to report */
    None = 'None',
}

/**
 * indicate call mute state (not to be confused with `member` mute state)
 */
export enum MuteState {
    /**
     * 🔴
     *
     * Participant is muted by the server. They cannot toggle their mute state
     */
    Disabled = 'Disabled',
    /**
     * 🟡
     *
     * Participant is muted and can toggle mute state
     */
    Muted = 'Muted',
    /**
     * 🟢
     *
     * Participant can be muted and can toggle mute state
     */
    Unmuted = 'Unmuted',
}

export type ChatActivityType =
    'joined' | 'left' | 'transferred' | 'removed' | 'empty-chat';

export type ChatActivityMessageParticipants = {
    isMe?: boolean;
    displayName: string;
};

export type RemovedParticipant = ChatActivityMessageParticipants;

export type ChatMessageID = string;

interface ChatMessageBase {
    id: ChatMessageID;
    at: Date;
    displayName: string;
}

/** A chat message sent by a participant to the conference or to an individual. */
export interface UserChatMessage extends ChatMessageBase {
    type: 'user-message';
    userId: ParticipantID;
    message: string;
    /** `true` while the message is sent but not yet confirmed by the server. */
    pending?: boolean;
}

/**
 * A system generated message describing a change in the conference.
 *
 * The displayed text is derived at render time from `activityType` and the
 * participant details, so that it follows the selected language.
 */
export interface ActivityChatMessage extends ChatMessageBase {
    type: 'activity';
    isMe: boolean;
    /** Cleared when consecutive activities are merged into a single message. */
    userId?: ParticipantID;
    activityType?: ChatActivityType;
    /**
     * Set when consecutive activities of the same type are merged into a single
     * message. `userId` is cleared at that point.
     */
    groupedParticipants?: ChatActivityMessageParticipants[];
    removedParticipant?: RemovedParticipant;
}

export type ChatMessage = UserChatMessage | ActivityChatMessage;

export type DirectChatMessages = Map<ParticipantID, UserChatMessage[]>;

export type UnseenUnreadDirectChatMessages = ChatMessageID[];

export enum ChatType {
    Direct = 'Direct',
    Everyone = 'Everyone',
}

export interface MeetingParticipant extends Participant {
    topic?: string;
}

export interface InMeetingParticipant extends MeetingParticipant {
    canChangeLayout: boolean;
    imageUrl?: string;
    isClientMuted: boolean;
    isMuted: boolean;
    isCameraMuted: boolean;
    isSpeaking?: boolean;
    kick?: () => void;
    mute?: () => void;
    muteVideo?: () => void;
    admit?: () => void;
    spotlight?: () => void;
    setRole?: () => void;
    lowerHand?: () => void;
    supportsDirectChat: boolean;
    highlightedCharacters?: {start: number; end: number}[];
    roomId?: RoomID;
}

export enum StreamQuality {
    Low = 'low',
    Medium = 'medium',
    High = 'high',
    VeryHigh = 'very-high',
    Auto = 'auto',
}

export enum VideoLayoutOptionValues {
    AdaptiveComposition = 'ac',
    Speaker7 = '1:7',
    Speaker21 = '1:21',
    Speaker221 = '2:21',
    Speaker33 = '1:33',
    Highlight = '1:0',
    Equal22 = '2x2',
    Equal33 = '3x3',
    Equal44 = '4x4',
    Equal55 = '5x5',
}

export interface PreviewMediaInput {
    error: DeviceError;
    preview: MediaDeviceInfoLike | undefined;
    applyChanges: () => void;
    setPreview: (device: MediaDeviceInfoLike) => void;
}

export enum NetworkState {
    Connected = 'connected',
    Reconnecting = 'reconnecting',
    Reconnected = 'reconnected',
}

export interface NormalizedStats {
    inbound: {
        audio?: InboundAudioMetrics;
        video?: InboundVideoMetrics;
        preso?: InboundVideoMetrics;
    };
    outbound: {
        audio?: OutboundAudioMetrics;
        video?: OutboundVideoMetrics;
        preso?: OutboundVideoMetrics;
    };
}

export const selfviewAspectRatioValues = [
    'camera-step',
    'in-meeting',
    'ready-to-join-express',
    'ready-to-join-step',
    'force-landscape',
] as const;
export type SelfviewAspectRatios = (typeof selfviewAspectRatioValues)[number];

export enum BreakoutRoomAssignmentMode {
    Automatically = 'Automatically',
    Manually = 'Manually',
}

export enum BreakoutRoomsScreen {
    ModeAssignment = 0,
    InitialConfiguration = 1,
    RoomsOpened = 2,
    EditConfiguration = 3,
}

export enum BreakoutRoomVariant {
    Setup = 'setup',
    Preview = 'preview',
    Edit = 'edit',
}

export type BreakoutRoomName = string;
export type BreakoutRoomId = RoomID;
export type BreakoutParticipants = Map<RoomID, InMeetingParticipant[]>;
export type BreakoutParticipantsIdentities = Map<
    BreakoutRoomId,
    ParticipantID[]
>;
export type BreakoutRoomNames = Map<BreakoutRoomId, BreakoutRoomName>;

export type BreakoutsEditPanelSave = {
    move: Map<BreakoutRoomId, BreakoutParticipantsIdentities>; // {oldRoomId: {newRoom1Id: [], newRoom2Id: []}}
    addRoom: Map<BreakoutRoomName, BreakoutParticipantsIdentities>;
    renameRoom: Map<BreakoutRoomId, BreakoutRoomName>;
    removeRoom: BreakoutRoomId[];
};

export interface BreakoutAskingForHelpStatus {
    roomId: RoomID;
    breakoutName: string;
    askForHelpTime: number;
}

export type EpochTimeStampSeconds = number;

export type ParticipantList = InMeetingParticipant[];
export type RoomTuple = [RoomID, ParticipantList];

export type Unsubscribe = () => void;
export type OnChange = () => void;
export type Subscribe = (onChange: OnChange) => Unsubscribe;

export enum CallHistoryDirection {
    Incoming = 'Incoming',
    Outgoing = 'Outgoing',
    Missed = 'Missed',
}

export type MeetingId = string;

type CallHistoryItem = {
    title?: string;
    meetingId: MeetingId;
    avatarImageUrl?: string;
};

export type CallHistoryRecentItem = CallHistoryItem & {
    direction: CallHistoryDirection;
    dateAndTime: string;
    succeeded: boolean;
    isExpired?: boolean;
};

export type CallHistoryFavoriteItem = CallHistoryItem & {
    isBranding?: boolean;
};

export type SuggestionItem = CallHistoryItem & {
    isFavorite: boolean;
    isBrandingFavorite?: boolean;
};
